import {
  MouseEventHandler,
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react'

import ReplyIcon from '@heroicons/react/outline/esm/ReplyIcon.js'
import ThumbUpIcon from '@heroicons/react/outline/esm/ThumbUpIcon.js'
import TrashIcon from '@heroicons/react/outline/esm/TrashIcon.js'
import { clsx } from 'clsx'

import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import { PinnedInto, Post, PostStatus } from '@tribeplatform/gql-client/types'
import { useRouter } from '@tribeplatform/react-sdk'
import { useAuthMember, useReplies } from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'
import { Icon } from '@tribeplatform/react-ui-kit/Icon'
import { SpinnerIcon } from '@tribeplatform/react-ui-kit/icons'
import { Link } from '@tribeplatform/react-ui-kit/Link'
import { Tooltip } from '@tribeplatform/react-ui-kit/Tooltip'

import { MemberAvatar } from '../common/components/index.js'
import { Time } from '../common/components/Time.js'
import { useLogin } from '../common/hooks/useLogin.js'
import { useQuery } from '../common/hooks/useQuery.js'
import { dayjs } from '../common/lib/dayjs.js'
import { useI18n, useStaticIntl, T } from '../i18n/index.js'
import { MemberBadgeLocation, MemberBadges } from '../MemberBadge/index.js'
import { SingleReaction } from '../Post/Actions/SingleReaction.js'
import { PostHiddenIndicator } from '../Post/PostHiddenIndicator.js'
import { getListFromParticipants } from '../Post/PostInfoBar.js'
import { PostPendingIndicator } from '../Post/PostPendingIndicator.js'
import { ReplyBar, ReplyBarRef } from '../Post/ReplyBar.js'
import { ReplyContent } from './ReplyContent.js'
import { ReplyOptions } from './ReplyOptions.js'

let isInternetExploreBrowser = false
if (typeof window !== 'undefined') {
  const userAgentSrting: string = window.navigator?.userAgent?.toLowerCase()
  isInternetExploreBrowser = userAgentSrting.indexOf('MSIE ') > 0
}

export const Reply = ({
  reply,
  canReply,
  readonly = false,
  onReply,
  context,
  post,
}: {
  reply: Post
  canReply: boolean
  readonly?: boolean
  onReply?: (e: MouseEventHandler<HTMLAnchorElement>) => void
  context?: 'post'
  post?: Post
}) => {
  const { $t } = useI18n()
  const participantsSummary = useStaticIntl(getListFromParticipants)
  const { Link: ReactLink } = useRouter()
  const replyRef = useRef<ReplyBarRef>()
  const replyContainerRef = useRef<HTMLDivElement>()
  const limit = 5
  const [showReply, setShowReply] = useState(false)
  const [edit, setEdit] = useState(false)
  const { isLoggedIn, showLogin } = useLogin()
  const { data: authMember } = useAuthMember()

  const member = reply?.owner?.member

  const { data, fetchNextPage, refetch, isFetching } = useReplies({
    variables: {
      limit,
      postId: reply.id,
      reverse: true,
    },
    fields: {
      owner: {
        member: { profilePicture: 'basic', badges: 'all' },
      },
      embeds: 'basic',
      mentions: 'basic',
      attachments: 'basic',
      authMemberProps: 'all',
      reactions: { fields: 'all', variables: { limit: 10 } },
    },
    useInfiniteQueryOptions: {
      enabled: false,
      refetchOnMount: 'always',
      placeholderData: useMemo(() => {
        if (reply?.replies?.totalCount > 0) {
          return {
            pageParams: [],
            pages: [reply.replies],
          }
        }
      }, [reply?.replies]),
    },
  })

  const query = useQuery()
  const { nodes: replies } = simplifyPaginatedResult<Post>(data)
  const unloadedReplies = Math.min(
    limit,
    reply.totalRepliesCount - replies.length,
  )

  const finishEditing = useCallback(() => setEdit(false), [])

  const _onReply = useCallback(
    e => {
      if (onReply) {
        return onReply(e)
      }
      if (!isLoggedIn) {
        e.preventDefault()
        return showLogin()
      }

      setShowReply(true)
      refetch({ refetchPage: (page, index) => index === 0 })
      // to re-expand it
      if (showReply && replyRef.current) {
        replyRef.current.expand(true)
      }
      // to jump into the input box
      setTimeout(() => {
        if (replyRef) {
          replyRef.current?.scrollIntoView()
        }
      }, 100)
      // to jump again after loading the replies first page
      setTimeout(() => {
        if (replyRef) {
          replyRef.current?.scrollIntoView()
        }
      }, 1000)
    },
    [onReply, isLoggedIn, refetch, showLogin, showReply],
  )

  const isHighlightedReply = reply.id === query?.highlight

  useEffect(() => {
    const replyContainer = replyContainerRef?.current
    if (isHighlightedReply && replyContainer) {
      const replyBoundingClientRect = replyContainer.getBoundingClientRect()
      // SCROLL_OFFSET here just scroll a bit past the reply to bring it more into the view
      if (!isInternetExploreBrowser) {
        window.scrollTo({
          top: replyBoundingClientRect?.top,
          behavior: 'smooth',
        })
      }
      // intenet explore doesn't support options(top,behavior)
      else {
        window.scrollTo(0, replyBoundingClientRect?.top)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // if we add reply as depednecy it casues an issue where page scroll jumps between top and to the reply
  }, [query?.highlight])

  if (reply) {
    const [canCreateReply, canHidePost] = hasScopesPermission(reply, [
      'createReply',
      'hidePost',
    ])
    if (!canHidePost && reply.isHidden) {
      return null
    }

    // TODO: This should be removed when hidden replies are handled properly from the backend.
    if (!canCreateReply || !reply.authMemberProps.availableReplyTypes?.length) {
      canReply = false
    }
  }

  const isPinnedReply = reply?.pinnedInto?.includes(PinnedInto.post)

  return (
    <div
      ref={replyContainerRef}
      className={clsx(
        'flex space-s-3 sm:space-s-4',
        (isHighlightedReply || (context === 'post' && isPinnedReply)) && [
          'rounded-base',
          '-mx-2 px-2 -my-2.5 py-2.5',
          isHighlightedReply && 'ring ring-offset-0 ring-action-primary',
          context === 'post' && isPinnedReply && 'bg-surface-positive-subdued',
        ],
      )}
      key={reply.id}
    >
      <div className="shrink-0">
        <div className="hidden sm:block">
          <MemberAvatar size="lg" member={member} clickable />
        </div>
        <div className="sm:hidden">
          <MemberAvatar size="md" member={member} clickable />
        </div>
      </div>
      <div className="min-w-0 grow max-w-full -mt-1 pt-1">
        <div>
          <div className="flex justify-between gap-2">
            <div className="flex grow min-w-0 max-w-full gap-x-2 gap-y-0.5 flex-wrap">
              <ReactLink
                variant="inherit"
                href={member?.relativeUrl}
                className="font-medium text-content shrink-0 max-w-full focus-visible:z-10"
                translate="no"
                truncate
                title={member?.name}
              >
                {member?.name}
              </ReactLink>
              <MemberBadges
                member={member}
                size="sm"
                location={MemberBadgeLocation.PostMemberBar}
              />
            </div>
            <div className="shrink-0 flex items-center">
              {reply.status === PostStatus.BLOCKED && <PostPendingIndicator />}
              {reply.isHidden && <PostHiddenIndicator isReply />}
              {!readonly && (
                <ReplyOptions
                  onEdit={() => setEdit(true)}
                  reply={reply}
                  post={post}
                />
              )}
            </div>
          </div>
          <div className="text-sm text-content-subdued flex max-w-full whitespace-nowrap mt-0.5">
            <ReactLink href={reply?.relativeUrl} variant="inherit" truncate>
              {reply?.status === PostStatus.DELETED && (
                <span>
                  <span className="text-action-destructive inline-flex items-center">
                    <span>
                      <T id="Generics.Deleted" defaultMessage="Deleted" />
                    </span>
                    <TrashIcon className="w-4 h-4 ms-0.5" />
                  </span>
                  {/* eslint-disable-next-line react/jsx-no-literals */}
                  <span>&nbsp;·&nbsp;</span>
                </span>
              )}
              <Time dateTime={reply.createdAt}>
                {dayjs(reply.createdAt).fromNow()}
              </Time>
              {member?.tagline ? (
                <span className="truncate"> · {member?.tagline}</span>
              ) : null}
            </ReactLink>
          </div>
        </div>
        <div className="mt-2">
          <ReplyContent
            reply={reply}
            edit={edit}
            onCancel={finishEditing}
            onEdit={finishEditing}
            context={
              context === 'post' && isPinnedReply ? 'pinnedReply' : 'reply'
            }
          />
        </div>
        {!readonly && (
          <div className="flex space-s-5 text-sm">
            <Tooltip>
              <Tooltip.Trigger>
                <SingleReaction
                  Icon={<ThumbUpIcon className="w-4 h-4" />}
                  post={reply}
                  reaction="+1"
                  className="font-medium"
                  simple
                  text={
                    reply.reactionsCount > 0
                      ? $t(
                          {
                            id: 'Generics.Likes.Plural',
                            defaultMessage:
                              '{likes_count, plural, zero {No likes} one {# like} other {# likes}}',
                          },
                          {
                            likes_count: reply.reactionsCount,
                          },
                        )
                      : $t({
                          id: 'Generics.Like.Verb',
                          defaultMessage: 'Like',
                        })
                  }
                />
              </Tooltip.Trigger>
              {reply.reactions && (
                <Tooltip.Panel>
                  {participantsSummary(
                    reply.reactions?.flatMap(
                      reaction => reaction.participants?.nodes || [],
                    ),
                    reply.reactionsCount,
                    authMember,
                  )}
                </Tooltip.Panel>
              )}
            </Tooltip>
            {canReply && (
              <Link
                as="button"
                variant="neutral"
                onClick={_onReply}
                className="font-medium"
              >
                <T id="Generics.Reply" defaultMessage="Reply" />
              </Link>
            )}
          </div>
        )}
        {unloadedReplies > 0 ? (
          <div className="flex items-center mt-4 space-s-3">
            <Link
              variant="inherit"
              className="text-sm leading-5 font-medium text-content-subdued flex items-center space-s-1"
              onClick={() => {
                fetchNextPage()
              }}
              title={$t(
                {
                  id: 'Reply.TotalRepliesOf',
                  defaultMessage: 'total replies of {total_replies_count}',
                },
                { total_replies_count: reply.totalRepliesCount },
              )}
            >
              <Icon className="w-4 h-4 rotate-180">
                <ReplyIcon />
              </Icon>
              <span>
                <T
                  defaultMessage="{replies_count, plural, =1 {# reply} other {# replies}}"
                  id="Reply.Count"
                  values={{
                    replies_count: unloadedReplies,
                  }}
                />
              </span>
            </Link>
            {isFetching && <SpinnerIcon className="animate-spin" />}
          </div>
        ) : null}
        {replies?.length > 0 || (canReply && showReply) ? (
          <div className="relative flex flex-col space-y-6 mt-6 p-2">
            {replies
              ?.map(reply => (
                <Reply
                  reply={reply}
                  key={reply.id}
                  canReply
                  readonly={readonly}
                  onReply={_onReply}
                />
              ))
              ?.reverse()}
            {canReply && showReply && (
              <ReplyBar ref={replyRef} post={reply} defaultExpand />
            )}
          </div>
        ) : null}
      </div>
    </div>
  )
}
